import { lazy, Suspense } from "react";
import type { AlbumDocument } from "../domain/project";
import { BookAlbumPreview } from "./BookAlbumPreview";
import { CircularGalleryPrototype } from "./CircularGalleryPrototype";
import { DepthPhotoGalleryPrototype } from "./DepthPhotoGalleryPrototype";
import { PhotoFrame } from "./PhotoFrame";

const CinemaAlbumPreview = lazy(() => import("./CinemaAlbumPreview").then(({ CinemaAlbumPreview: Preview }) => ({ default: Preview })));
const DeckAlbumPreview = lazy(() => import("./DeckAlbumPreview").then(({ DeckAlbumPreview: Preview }) => ({ default: Preview })));
const FocusAlbumPreview = lazy(() => import("./FocusAlbumPreview").then(({ FocusAlbumPreview: Preview }) => ({ default: Preview })));
const ScrapbookAlbumPreview = lazy(() => import("./ScrapbookAlbumPreview").then(({ ScrapbookAlbumPreview: Preview }) => ({ default: Preview })));
const AccordionAlbumPreview = lazy(() => import("./AccordionAlbumPreview").then(({ AccordionAlbumPreview: Preview }) => ({ default: Preview })));
const DriftWallAlbumPreview = lazy(() => import("./DriftWallAlbumPreview").then(({ DriftWallAlbumPreview: Preview }) => ({ default: Preview })));

const previewLoading = <section className="preview-loading" aria-label="正在加载阅读预览">正在准备阅读……</section>;

interface AlbumPreviewProps {
  document: AlbumDocument;
  urls: Record<string, string>;
}

export function AlbumPreview({ document, urls }: AlbumPreviewProps) {
  if (document.template === "book") return <BookAlbumPreview document={document} urls={urls} />;
  if (document.template === "cinema") {
    return <Suspense fallback={previewLoading}><CinemaAlbumPreview document={document} urls={urls} /></Suspense>;
  }
  if (document.template === "deck") {
    return <Suspense fallback={previewLoading}><DeckAlbumPreview document={document} urls={urls} /></Suspense>;
  }
  if (document.template === "focus") {
    return <Suspense fallback={previewLoading}><FocusAlbumPreview document={document} urls={urls} /></Suspense>;
  }
  if (document.template === "scrapbook") {
    return <Suspense fallback={previewLoading}><ScrapbookAlbumPreview document={document} urls={urls} /></Suspense>;
  }
  if (document.template === "accordion") {
    return <Suspense fallback={previewLoading}><AccordionAlbumPreview document={document} urls={urls} /></Suspense>;
  }
  if (document.template === "drift") {
    return <Suspense fallback={previewLoading}><DriftWallAlbumPreview document={document} urls={urls} /></Suspense>;
  }
  if (document.template === "circular") return <CircularGalleryPrototype document={document} urls={urls} embedded headingId="circular-preview-title" />;
  if (document.template === "depth") return <DepthPhotoGalleryPrototype document={document} urls={urls} embedded headingId="depth-preview-title" />;
  const cover = document.coverAssetId ? urls[document.coverAssetId] : undefined;
  return (
    <article className={`album-preview template-${document.template}`}>
      <header className="preview-cover">
        <div className="preview-cover-copy">
          <span className="preview-mark">PHOTO FOLIO</span>
          <h2>{document.title}</h2>
          <p>{document.subtitle}</p>
          <div className="preview-meta">
            {document.date ? <span>{document.date}</span> : null}
            {document.location ? <span>{document.location}</span> : null}
          </div>
        </div>
        <PhotoFrame src={cover} alt={`${document.title}封面`} presentation={document.pages.find((page) => page.assetId === document.coverAssetId)?.presentation} className="preview-cover-media" decoding="async" placeholder={<div className="cover-placeholder">等待第一张照片</div>} />
      </header>
      <div className="preview-pages">
        {document.pages.map((page) => (
          <figure key={page.id}>
            <PhotoFrame src={urls[page.assetId]} alt={page.title || "影集照片"} presentation={page.presentation} className="preview-page-media" loading="lazy" decoding="async" />
            <figcaption>
              <div>
                <small>{[page.date, page.location].filter(Boolean).join(" / ")}</small>
                <h3>{page.title}</h3>
              </div>
              <p>{page.caption || "这张照片还没有说明。"}</p>
            </figcaption>
          </figure>
        ))}
      </div>
    </article>
  );
}
